import { PDF_DPI, SVG_PPI } from "common/constants/pdfSettings";
import type { DotsPerInch, NominalNumberTypeName } from "common/types/nominalNumbersTypes";
import {
  type FeetSquared,
  type InchesCubed,
  type InchesSquared,
  type LevelInches,
  type NominalNumber,
  type SheetInches,
  type SheetPixels,
  type SheetPoints,
  type WorldInches,
  type OneHundredthMillimeters,
  type WorldFeet,
  type FeetCubed,
  type YardsCubed,
  type Slope,
  type UnsignedNumber,
  type Percent,
  type PixelsPerInch,
  NominalNumberTypeNames,
} from "common/types/nominalNumbersTypes";

export function isNominalNumberTypeName(n: string): n is NominalNumberTypeName {
  return NominalNumberTypeNames.includes(n as NominalNumberTypeName);
}

export function nominalNumberNew<T extends NominalNumber>(n: number) {
  return n as T;
}

export function worldInchesNew(n: number) {
  return nominalNumberNew<WorldInches>(n);
}

export function worldFeetNew(n: number) {
  return nominalNumberNew<WorldFeet>(n);
}

export function worldInchesTolevel(n: WorldInches, floorElevation: WorldInches) {
  return levelInchesNew(n - floorElevation);
}

export function worldInchesToSheet(n: WorldInches, viewScaleFactor: number) {
  return sheetInchesNew(n * viewScaleFactor);
}

export function levelInchesNew(n: number) {
  return nominalNumberNew<LevelInches>(n);
}

export function levelInchesToWorld(n: LevelInches, floorElevation: WorldInches) {
  return worldInchesNew(floorElevation + n);
}

export function worldInchesToLevel(n: WorldInches, floorElevation: WorldInches) {
  return levelInchesNew(n - floorElevation);
}

export function inchesSquaredNew(n: number) {
  return nominalNumberNew<InchesSquared>(n);
}

export function feetSquaredNew(n: number) {
  return nominalNumberNew<FeetSquared>(n);
}

export function inchesCubedNew(n: number) {
  return nominalNumberNew<InchesCubed>(n);
}

export function feetCubedNew(n: number) {
  return nominalNumberNew<FeetCubed>(n);
}

export function yardsCubedNew(n: number) {
  return nominalNumberNew<YardsCubed>(n);
}

export function sheetInchesNew(n: number) {
  return nominalNumberNew<SheetInches>(n);
}

export function sheetInchesToPoints(n: SheetInches, dpi: DotsPerInch = PDF_DPI) {
  return nominalNumberNew<SheetPoints>(n * dpi);
}

export function sheetInchesToPixels(n: SheetInches, ppi: PixelsPerInch = SVG_PPI) {
  return nominalNumberNew<SheetPixels>(n * ppi);
}

export function sheetInchesToWorld(n: SheetInches, viewScaleFactor: number) {
  return nominalNumberNew<WorldInches>(n / viewScaleFactor);
}

export function sheetPointsNew(n: number) {
  return nominalNumberNew<SheetPoints>(n);
}

export function sheetPointsToInches(n: SheetPoints, dpi: DotsPerInch = PDF_DPI) {
  return nominalNumberNew<SheetInches>(n / dpi);
}

export function sheetPointsToPixels(n: SheetPoints) {
  return sheetInchesToPixels(sheetPointsToInches(n));
}

export function sheetPointsToWorld(n: SheetPoints, viewScaleFactor: number) {
  return nominalNumberNew<WorldInches>(sheetInchesToWorld(sheetPointsToInches(n), viewScaleFactor));
}

export function sheetPixelsNew(n: number) {
  return nominalNumberNew<SheetPixels>(n);
}

export function sheetPixelsToInches(n: SheetPixels, ppi: PixelsPerInch = SVG_PPI) {
  return nominalNumberNew<SheetInches>(n / ppi);
}

export function sheetPixelsToPoints(n: SheetPixels, ppi: PixelsPerInch = SVG_PPI, dpi: DotsPerInch = PDF_DPI) {
  return sheetInchesToPoints(sheetPixelsToInches(n, ppi), dpi);
}

export function oneHundredthMillimetersNew(n: number) {
  return nominalNumberNew<OneHundredthMillimeters>(n);
}

export function slopeNew(n: number) {
  return nominalNumberNew<Slope>(n);
}

export function percentNew(n: number): Percent {
  return nominalNumberNew<Percent>(n);
}

// 0.01 mm & 1 mm = 0.03937 inches
const ONE_HUNDREDTH_MILLIMETER_TO_INCH = 0.01 * 0.03937;

export function oneHundredthMillimetersToInches(n: OneHundredthMillimeters) {
  return nominalNumberNew<SheetInches>(n * ONE_HUNDREDTH_MILLIMETER_TO_INCH);
}

export function numAdd<T extends NominalNumber>(a: T, b: T): T {
  return nominalNumberNew<T>(a + b);
}

export function numSub<T extends NominalNumber>(a: T, b: T): T {
  return nominalNumberNew<T>(a - b);
}

export function numMul<T extends NominalNumber>(a: T, b: number): T {
  return nominalNumberNew<T>(a * b);
}

export function numDiv<T extends NominalNumber>(a: T, b: number): T {
  return nominalNumberNew<T>(a / b);
}

export function numMin<T extends NominalNumber>(a: T, b: T): T {
  return nominalNumberNew<T>(Math.min(a, b));
}

export function numMax<T extends NominalNumber>(a: T, b: T): T {
  return nominalNumberNew<T>(Math.max(a, b));
}

export function numRound<T extends NominalNumber>(n: T): T {
  return nominalNumberNew<T>(Math.round(n));
}

/** Conversions */

export function worldInchesFromWorldFeet(n: WorldFeet): WorldInches {
  return worldInchesNew(n * 12);
}

export function worldFeetFromWorldInches(n: WorldInches): WorldFeet {
  return worldFeetNew(n / 12);
}

export function inchesSquaredFromFeetSquared(n: FeetSquared): InchesSquared {
  return inchesSquaredNew(n * 144);
}

export function feetSquaredFromInchesSquared(n: InchesSquared): FeetSquared {
  return feetSquaredNew(n / 144);
}

export function feetFromWorldInches(n: WorldInches): WorldFeet {
  return worldFeetNew(n / 12);
}

export function feetCubedFromInchesCubed(n: InchesCubed): FeetCubed {
  return feetCubedNew(n / (12 * 12 * 12));
}

export function yardsCubedFromInchesCubed(n: InchesCubed): YardsCubed {
  return yardsCubedNew(n / (36 * 36 * 36));
}

export function yardsCubedFromFeetCubed(n: FeetCubed): YardsCubed {
  return yardsCubedNew(n / (3 * 3 * 3));
}

export function feetCubedFromYardsCubed(n: YardsCubed): FeetCubed {
  return feetCubedNew(n * (3 * 3 * 3));
}

/**
 * Converts a number to a unsigned number type
 */
export function unsignedNumberNew(value: number) {
  return nominalNumberNew<UnsignedNumber>(Math.abs(value));
}
